import React, { useState, useRef, useEffect } from "react";
import {
  auth,
  googleProvider,
} from "../../firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
  OAuthProvider,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { FaGoogle } from "react-icons/fa";
import { IoLogoApple } from "react-icons/io5";
import { FiEye, FiEyeOff } from "react-icons/fi";
import LockResetIcon from "@mui/icons-material/LockReset";
import {
  getFriendlyFirebaseErrorMessage,
  getFriendlyPasswordResetErrorMessage,
  getPasswordResetContinueUrl,
  isValidEmailFormat,
} from "../../utils/auth";
import { loginUser, socialSignUp } from "../../utils/utils";
import {
  getOnboardingRedirectPath,
  shouldTreatLoginFailureAsNewSocialUser,
} from "../../utils/onboardingNavigation";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaSmsSending, setMfaSmsSending] = useState(false);

  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { setUserId, setUserRepsBioguideID, loadUserContext, refreshFirebaseUser } = useUser();

  // Signup hands off with `verificationSent` so the reason for landing here is
  // visible instead of dropping the user on a bare login form.
  useEffect(() => {
    if (location.state?.verificationSent) {
      setStatus(
        "Check your inbox for the verification link, then log in below. If you already verified, just sign in."
      );
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const extractName = (user) => {
    const displayName = user.displayName || "";
    const [first, ...rest] = displayName.split(" ");
    return { first_name: first || "", last_name: rest.join(" ") || "" };
  };

  const finishLogin = async (firebaseUser) => {
    // Only a password-backed account can be unverified; provider-issued emails
    // (Google/Apple) are verified upstream. Checking for the absence of the
    // password provider avoids skipping the check for accounts that have both.
    const hasPasswordProvider = firebaseUser.providerData?.some(
      (p) => p.providerId === "password"
    );
    const isSocialOnly = !hasPasswordProvider;

    if (hasPasswordProvider) {
      const refreshed = await refreshFirebaseUser();
      const userToCheck = refreshed ?? auth.currentUser ?? firebaseUser;
      if (!userToCheck?.emailVerified) {
        // Keep the session alive so the user can resend the link and continue;
        // signing out here strands anyone who never clicked the first email.
        navigate("/verify-email", { replace: true });
        return;
      }
    }

    try {
      const { userId, bioguideId } = await loginUser();
      setUserId(userId);
      setUserRepsBioguideID(bioguideId);
    } catch (loginErr) {
      if (isSocialOnly && shouldTreatLoginFailureAsNewSocialUser(loginErr.status)) {
        await socialSignUp(false, extractName(firebaseUser));
        const ctx = await loadUserContext();
        const destination = getOnboardingRedirectPath(ctx || null);
        navigate(destination, {
          replace: true,
          state: destination === "/setup-representative" ? { fromSignup: true } : undefined,
        });
        return;
      }
      throw loginErr;
    }

    const ctx = await loadUserContext();
    if (!ctx) {
      setError("Could not load your account. Please try again.");
      return;
    }
    navigate(getOnboardingRedirectPath(ctx), { replace: true });
  };

  const handleMfaError = async (err) => {
    if (err.code === "auth/multi-factor-auth-required") {
      const resolver = getMultiFactorResolver(auth, err);
      setMfaResolver(resolver);
      setMfaCode("");
      setMfaVerificationId(null);
      setError(null);

      const phoneHint = resolver.hints.find(
        (h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID
      );
      if (!phoneHint) {
        setError("No phone factor found. Please contact support.");
        return true;
      }

      setMfaSmsSending(true);
      try {
        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
          recaptchaVerifierRef.current = null;
        }
        if (recaptchaContainerRef.current) {
          recaptchaContainerRef.current.innerHTML = "";
        }
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
          size: "invisible",
        });
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const vId = await phoneAuthProvider.verifyPhoneNumber(
          { multiFactorHint: phoneHint, session: resolver.session },
          recaptchaVerifierRef.current
        );
        setMfaVerificationId(vId);
      } catch (smsErr) {
        setError(smsErr.message || "Failed to send verification SMS.");
      } finally {
        setMfaSmsSending(false);
      }

      return true;
    }
    return false;
  };

  const handleMfaVerify = async () => {
    if (!mfaResolver || !mfaVerificationId || mfaCode.length !== 6) return;
    setMfaLoading(true);
    setError(null);
    try {
      const credential = PhoneAuthProvider.credential(mfaVerificationId, mfaCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      const { user } = await mfaResolver.resolveSignIn(assertion);
      setMfaResolver(null);
      setMfaVerificationId(null);
      setMfaCode("");
      await finishLogin(user);
    } catch (err) {
      if (err.code === "auth/invalid-verification-code") {
        setError("Invalid code. Please check your SMS and try again.");
      } else {
        setError(err.message || "Verification failed.");
      }
    } finally {
      setMfaLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setStatus("");

    try {
      await setPersistence(auth, browserLocalPersistence);
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      await finishLogin(user);
    } catch (err) {
      if (!(await handleMfaError(err))) {
        setError(getFriendlyFirebaseErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProviderLogin = async (provider, name) => {
    setLoading(true);
    setError(null);
    setStatus("");
    try {
      await setPersistence(auth, browserLocalPersistence);
      const { user } = await signInWithPopup(auth, provider);
      await finishLogin(user);
    } catch (err) {
      if (!(await handleMfaError(err))) {
        setError(`${name} login failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword((p) => !p);

  const handleForgotPassword = async () => {
    setError(null);
    setStatus("");

    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setError("Please enter your email address first.");
      return;
    }
    if (!isValidEmailFormat(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, normalizedEmail, {
        url: getPasswordResetContinueUrl(),
        handleCodeInApp: false,
      });
      setStatus(
        "If an account exists with this email, a password reset link has been sent. Check your inbox and spam folder."
      );
    } catch (err) {
      setError(getFriendlyPasswordResetErrorMessage(err));
    } finally {
      setResetLoading(false);
    }
  };

  if (mfaResolver) {
    return (
      <div className="w-full">
        <div ref={recaptchaContainerRef} />
        {error && (
          <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight text-[#0f172a]">Two-Factor Authentication</h1>
          <p className="mt-2 text-sm text-[#334155]">
            {mfaSmsSending
              ? "Sending verification code..."
              : mfaVerificationId
                ? "Enter the 6-digit code sent to your phone via SMS."
                : "Preparing verification..."}
          </p>
        </div>

        {mfaVerificationId && (
          <div className="mt-6 flex flex-col items-center gap-4">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              autoFocus
              className="w-48 rounded-xl border border-[#cbd5e1] bg-white px-3 py-3 text-center text-2xl font-bold tracking-[0.5em] text-[#0f172a] placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#047857]/30 focus:border-[#047857]"
            />

            <button
              type="button"
              onClick={handleMfaVerify}
              disabled={mfaCode.length !== 6 || mfaLoading}
              className="inline-flex w-full max-w-[220px] items-center justify-center rounded-full bg-[#047857] px-5 py-3 text-base font-semibold text-white transition hover:bg-[#065f46] focus:outline-none focus:ring-4 focus:ring-[#047857]/30 disabled:opacity-60"
            >
              {mfaLoading ? (
                <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              ) : (
                "Verify"
              )}
            </button>
          </div>
        )}

        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => {
              setMfaResolver(null);
              setMfaVerificationId(null);
              setMfaCode("");
              setError(null);
            }}
            className="text-sm font-medium text-[#334155] hover:text-[#0f172a]"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div ref={recaptchaContainerRef} />
      {error && (
        <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-[#0f172a]">Welcome back</h1>
        <p className="mt-2 text-sm italic text-[#0f172a]">Your impact continues here.</p>
      </div>

      <form onSubmit={handleLogin} noValidate className="mt-6 space-y-5">
        <div className="flex flex-col">
          <label htmlFor="login-email" className="text-xs font-medium text-[#0f172a]">Email Address</label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            placeholder="you@example.com"
            className="mt-1 block w-full rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 text-base text-[#0f172a] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#047857]/30 focus:border-[#047857]"
          />
        </div>

        <div className="flex flex-col mb-2">
          <label htmlFor="login-password" className="text-xs font-medium text-[#0f172a]">Password</label>
          <div className="mt-1 relative">
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              placeholder="Enter your password"
              className="block w-full rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 pr-11 text-base text-[#0f172a] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#047857]/30 focus:border-[#047857]"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 grid w-11 place-items-center text-black hover:text-slate-700"
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || resetLoading}
          className="mt-1 inline-flex w-full items-center justify-center rounded-full bg-[#047857] px-5 py-3.5 text-base font-semibold text-white transition hover:bg-[#065f46] focus:outline-none focus:ring-4 focus:ring-[#047857]/30 disabled:opacity-60"
        >
          {loading ? (
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            "Login"
          )}
        </button>

        <button
          type="button"
          onClick={handleForgotPassword}
          disabled={loading || resetLoading}
          className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#047857]/30 bg-[#047857]/10 px-5 py-2.5 text-sm font-semibold text-[#047857] transition hover:bg-[#047857]/15 focus:outline-none focus:ring-4 focus:ring-[#047857]/20 disabled:opacity-60"
        >
          {resetLoading ? (
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            <>
              <LockResetIcon sx={{ fontSize: 18, color: "inherit" }} />
              Forgot Password
            </>
          )}
        </button>

        {status && (
          <div role="status" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
            {status}
          </div>
        )}
      </form>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-[#e2e8f0]" />
        <span className="px-2 text-xs font-medium uppercase tracking-wider text-[#64748b]">or</span>
        <span className="h-px flex-1 bg-[#e2e8f0]" />
      </div>

      <p className="text-center text-sm text-[#0f172a]">Sign in with a connected account</p>

      <div className="mt-4 flex justify-center gap-4">
        <button
          onClick={() => handleProviderLogin(googleProvider, "Google")}
          aria-label="Sign in with Google"
          className="social-btn"
        >
          <FaGoogle />
        </button>
        <button
          onClick={() => handleProviderLogin(new OAuthProvider("apple.com"), "Apple")}
          aria-label="Sign in with Apple"
          className="social-btn"
        >
          <IoLogoApple />
        </button>
      </div>

      <p className="mt-6 text-center text-sm text-[#334155]">
        Don&apos;t have an account?{" "}
        <button
          type="button"
          onClick={() => navigate("/auth/signup")}
          className="font-semibold text-[#047857] underline underline-offset-2 hover:text-[#065f46] focus:outline-none focus:ring-2 focus:ring-[#047857]/30 rounded"
        >
          Sign up
        </button>
      </p>
    </div>
  );
}
